/**
 * Cadastro e edição de fornecedor — rotas `/app/fornecedores/novo` e
 * `/app/fornecedores/:codigo/editar`.
 *
 * Espelho de `clientes/TelaCadastroCliente.tsx`, e de propósito: os dois são o
 * mesmo problema (uma pessoa jurídica com endereço, contato e condições) e
 * qualquer diferença entre eles seria acidente, não decisão.
 *
 * O JSDoc de lá explica por que isto virou rota em vez de continuar como modo
 * de `useState` dentro da listagem, e por que continua sendo tela cheia em vez
 * de gaveta. Vale igual aqui — com um agravante: o formulário de fornecedor
 * tem as categorias fornecidas e as condições de pagamento, que são blocos de
 * seleção múltipla e ocupam largura.
 */
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Botao, LinkBotao } from '../../componentes/Botao'
import { ErroApi } from '../../plataforma/api'
import { carregarFichaFornecedor, type FichaFornecedor } from '../../plataforma/fornecedores'
import { LayoutApp } from '../app/LayoutApp'
import { FormularioFornecedor, ID_FORMULARIO_FORNECEDOR } from './FormularioFornecedor'

export function TelaCadastroFornecedor() {
  return <Formulario />
}

export function TelaEdicaoFornecedor() {
  const { codigo = '' } = useParams<{ codigo: string }>()
  const [ficha, setFicha] = useState<FichaFornecedor | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erroDeCarga, setErroDeCarga] = useState('')

  useEffect(() => {
    const controle = new AbortController()
    setCarregando(true)

    carregarFichaFornecedor(codigo, controle.signal)
      .then((dados) => {
        setFicha(dados)
        setErroDeCarga('')
      })
      .catch((causa: unknown) => {
        if (causa instanceof DOMException && causa.name === 'AbortError') return
        setErroDeCarga(
          causa instanceof ErroApi ? causa.message : 'Não foi possível carregar o fornecedor.',
        )
      })
      .finally(() => setCarregando(false))

    return () => controle.abort()
  }, [codigo])

  if (carregando) {
    return (
      <LayoutApp largura="formulario" titulo="Editar fornecedor" subtitulo="Carregando…">
        <div className="h-[420px] w-full animate-pulse rounded-sistema bg-superficie" />
      </LayoutApp>
    )
  }

  if (erroDeCarga || !ficha) {
    return (
      <LayoutApp
        largura="formulario"
        trilha={[{ rotulo: 'Fornecedores', para: '/app/fornecedores' }, { rotulo: codigo }]}
        titulo="Editar fornecedor"
        subtitulo={codigo}
      >
        <div className="flex w-full flex-col gap-4">
          <div role="alert" className="rounded-sistema bg-erro-suave px-4 py-3 text-sm text-erro-texto">
            {erroDeCarga || 'Fornecedor não encontrado.'}
          </div>
          <div>
            <LinkBotao para="/app/fornecedores" variante="secundario" icone="arrow_back">
              Voltar aos fornecedores
            </LinkBotao>
          </div>
        </div>
      </LayoutApp>
    )
  }

  return <Formulario ficha={ficha} />
}

function Formulario({ ficha }: { ficha?: FichaFornecedor }) {
  const navegar = useNavigate()
  const [enviando, setEnviando] = useState(false)

  const editando = Boolean(ficha)

  return (
    <LayoutApp
      largura="formulario"
      trilha={[
        { rotulo: 'Fornecedores', para: '/app/fornecedores' },
        { rotulo: editando ? (ficha?.nome ?? 'Editar') : 'Novo fornecedor' },
      ]}
      voltarPara="auto"
      titulo={editando ? 'Editar fornecedor' : 'Cadastro de fornecedor'}
      subtitulo={
        editando
          ? `Ficha ${ficha?.codigo ?? ''}`
          : 'Quem abastece a loja, com contato e condições de compra'
      }
      acoes={
        <div className="flex items-center gap-2.5">
          <Botao variante="secundario" onClick={() => navegar(-1)}>
            Cancelar
          </Botao>
          <Botao type="submit" form={ID_FORMULARIO_FORNECEDOR} icone="check" disabled={enviando}>
            {enviando ? 'Salvando…' : editando ? 'Salvar alterações' : 'Salvar fornecedor'}
          </Botao>
        </div>
      }
    >
      <FormularioFornecedor
        ficha={ficha}
        aoEstadoMudar={setEnviando}
        aoSalvar={() => navegar('/app/fornecedores')}
      />
    </LayoutApp>
  )
}
